import { NavLink } from "react-router-dom";
import { LayoutDashboard, SlidersHorizontal, ScrollText, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface NavEntry {
  to: string;
  label: string;
  icon: LucideIcon;
  hint?: string;
}

const nav: NavEntry[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, hint: "Sync-Status" },
  { to: "/settings", label: "Einstellungen", icon: SlidersHorizontal, hint: "Lieferarten & Mail" },
  { to: "/logs", label: "Logs", icon: ScrollText, hint: "Live-Server-Logs" },
];

/**
 * Sidebar — schmaler Vertical-Nav. Brand-Mark oben, Nav-Liste mit
 * Icons + Label + sub-line Hint, Footer unten mit Version.
 *
 * Aesthetisch konsistent mit dem Header: warmer Near-Black,
 * Pulse-Emerald als Active-Akzent (links: 2 px vertikale Linie).
 */
export function Sidebar() {
  return (
    <aside className="hidden md:flex md:fixed md:inset-y-0 md:left-0 md:w-60 md:flex-col md:border-r md:border-border md:bg-background/90 md:backdrop-blur-md z-40">
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-border">
        <div className="relative h-7 w-7 shrink-0">
          <div className="absolute inset-0 rounded-sm bg-signal-live/15 ring-1 ring-signal-live/40" />
          <div className="absolute inset-[6px] rounded-[2px] bg-signal-live shadow-[0_0_12px_-2px] shadow-signal-live/70" />
        </div>
        <div className="flex flex-col leading-none min-w-0">
          <span className="font-serif text-[13px] tracking-tight text-foreground">
            TCM Business
          </span>
          <span className="mt-1 eyebrow">Operator</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            className={({ isActive }) =>
              cn(
                "relative flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors",
                isActive
                  ? "bg-signal-live/10 text-foreground"
                  : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span className="absolute -left-3 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-full bg-signal-live shadow-[0_0_8px_-1px] shadow-signal-live" />
                )}
                <n.icon className="h-4 w-4 shrink-0" />
                <span className="flex flex-col leading-tight min-w-0">
                  <span>{n.label}</span>
                  {n.hint && (
                    <span className="eyebrow text-[9px] text-muted-foreground/80">
                      {n.hint}
                    </span>
                  )}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="px-5 py-4 border-t border-border space-y-1">
        <p className="eyebrow">Version</p>
        <p className="font-mono text-[10px] text-foreground/80">v0.2.0</p>
      </div>
    </aside>
  );
}

/**
 * Mobile-Top-Nav: schlanke Pille mit den Nav-Einträgen, sichtbar
 * nur unter md-Breakpoint. Sidebar ist da hidden.
 */
export function MobileNav() {
  return (
    <nav className="md:hidden sticky top-14 z-30 flex gap-1 overflow-x-auto border-b border-border bg-background/85 px-3 py-2 backdrop-blur-md">
      {nav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === "/"}
          className={({ isActive }) =>
            cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-mono uppercase tracking-wider transition-colors whitespace-nowrap",
              isActive
                ? "border-signal-live/40 bg-signal-live/10 text-signal-live"
                : "border-border bg-muted/30 text-muted-foreground hover:text-foreground",
            )
          }
        >
          <n.icon className="h-3 w-3" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}
